import { type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  type PimProductV11,
  type PimProductV1Flags,
  PimProductV1LifestyleProductEnum,
  type PrescriptionPrescriptionTypeEnum,
} from '@redteclab/api/clients/bully'

import { useOpenableControls } from '../../../../../generic-hooks'
import { useGlobalConfigContext } from '../../../../../global-config'
import { IconCircleInfoFilled } from '../../../../../icons'
import {
  ProductPrescriptionDialogContraceptive,
  ProductPrescriptionDialogLifestyle,
  ProductPrescriptionDialogMedicalDevice,
} from '../../../../../product'
import { TENANT } from '../../../../../tenant'
import { prescriptionAccessorIsInsuranceOrFree } from '../../../../model/prescriptionAccessor/prescriptionAccessorIsInsuranceOrFree'
import { productAccessorIsContraceptive } from '../../../../model/productAccessor/productAccessorIsContraceptive'
import { PRODUCT_TEMPLATES } from '../../../../model/productTemplates'
import { useProductPageContext } from '../../../productPageContext'

const lifestyleProductTypes = new Set<PimProductV1LifestyleProductEnum>([
  PimProductV1LifestyleProductEnum.True,
  PimProductV1LifestyleProductEnum.TrueWithExceptions,
])
const shouldRenderMedicalDeviceInfo = ({
  currentPrescriptionType,
  productFlags,
  tenant,
}: {
  currentPrescriptionType: PrescriptionPrescriptionTypeEnum | null
  productFlags?: PimProductV1Flags
  tenant: TENANT
}): boolean =>
  tenant === TENANT.COM &&
  Boolean(productFlags?.medical_device.active) &&
  prescriptionAccessorIsInsuranceOrFree(currentPrescriptionType)

const shouldRenderLifestyleProductInfo = ({
  currentPrescriptionType,
  lifestyleProduct,
  productTemplate,
  tenant,
}: {
  currentPrescriptionType: PrescriptionPrescriptionTypeEnum | null
  lifestyleProduct?: PimProductV1LifestyleProductEnum
  productTemplate: PRODUCT_TEMPLATES
  tenant: TENANT
}): boolean =>
  tenant === TENANT.COM &&
  productTemplate !== PRODUCT_TEMPLATES.OTX &&
  Boolean(lifestyleProduct && lifestyleProductTypes.has(lifestyleProduct)) &&
  prescriptionAccessorIsInsuranceOrFree(currentPrescriptionType)

const shouldRenderContraceptiveProductInfo = ({
  currentPrescriptionType,
  currentVariant,
  productTemplate,
  tenant,
}: {
  currentPrescriptionType: PrescriptionPrescriptionTypeEnum | null
  currentVariant: PimProductV11
  productTemplate: PRODUCT_TEMPLATES
  tenant: TENANT
}): boolean =>
  tenant === TENANT.COM &&
  productTemplate === PRODUCT_TEMPLATES.RX &&
  productAccessorIsContraceptive(currentVariant) &&
  prescriptionAccessorIsInsuranceOrFree(currentPrescriptionType)

const getPriceInfoContent = ({
  currentPrescriptionType,
  currentVariant,
  productTemplate,
  tenant,
}: {
  currentPrescriptionType: PrescriptionPrescriptionTypeEnum | null
  currentVariant: PimProductV11
  productTemplate: PRODUCT_TEMPLATES
  tenant: TENANT
}): 'contraceptiveProduct' | 'lifestyleProduct' | 'medicalDevice' | null => {
  if (
    shouldRenderContraceptiveProductInfo({
      currentPrescriptionType,
      currentVariant,
      productTemplate,
      tenant,
    })
  ) {
    return 'contraceptiveProduct'
  }

  if (
    shouldRenderLifestyleProductInfo({
      currentPrescriptionType,
      lifestyleProduct: currentVariant.lifestyle_product,
      productTemplate,
      tenant,
    })
  ) {
    return 'lifestyleProduct'
  }

  if (
    shouldRenderMedicalDeviceInfo({
      currentPrescriptionType,
      productFlags: currentVariant.flags,
      tenant,
    })
  ) {
    return 'medicalDevice'
  }

  return null
}

export const ProductAddToCartGroupPriceInfo: FC = () => {
  const controls = useOpenableControls()
  const { currentPrescriptionType, currentVariant, productTemplate } =
    useProductPageContext()
  const { tenant } = useGlobalConfigContext()

  const priceInfoContent = getPriceInfoContent({
    currentPrescriptionType,
    currentVariant,
    productTemplate,
    tenant,
  })

  if (!priceInfoContent) {
    return null
  }

  return (
    <>
      <button
        className="my-3 flex rounded bg-light-primary-low px-5 py-3 text-left text-dark-brand hover:text-dark-hover-brand"
        onClick={controls.handleOpen}
        type="button"
      >
        <IconCircleInfoFilled className="mr-3 text-3xl" />
        <div className="text-m">
          <FormattedMessage id="prescription.infoBox.infoLabel" />
          <div className="mt-2 font-bold underline">
            <FormattedMessage id="prescription.infoBox.moreInformation" />
          </div>
        </div>
      </button>
      {priceInfoContent === 'contraceptiveProduct' && (
        <ProductPrescriptionDialogContraceptive
          isOpen={controls.isOpen}
          onClose={controls.handleClose}
        />
      )}
      {priceInfoContent === 'lifestyleProduct' && (
        <ProductPrescriptionDialogLifestyle
          isOpen={controls.isOpen}
          onClose={controls.handleClose}
        />
      )}
      {priceInfoContent === 'medicalDevice' && (
        <ProductPrescriptionDialogMedicalDevice
          isOpen={controls.isOpen}
          onClose={controls.handleClose}
        />
      )}
    </>
  )
}
